/**
 * MCP 연결 화면(§8.2): 상태 · 연결 방법(클라이언트별 스니펫) · 도구 목록 · 플레이그라운드 · 문제 해결.
 * 토큰은 어디에도 보이지 않는다(ADV-F07). 실행 파일 경로는 Rust가 준다(웹뷰가 추측하지 않는다).
 * 주 행동은 스니펫 「복사」 하나다.
 */
import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ArrowClockwise16Regular, Checkmark16Regular, Copy16Regular, Play16Regular } from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSettingsHub } from "@/shell/settings-hub";
import { ConfirmDialog } from "@/components/app-dialog";
import { FuiButton, FuiDropdown, FuiTextarea } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { PageHeaderTitle, SectionTitle } from "@/components/ui-typography";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConnectionBadge } from "@/desktop/ConnectionBadge";
import catalog from "@/desktop/mcp-tools.json";
import { invoke, isDesktop } from "@/desktop/runtime";
import { useConnectionStatus } from "@/desktop/use-connection-status";
import { formatAgo } from "@/lib/date-format";
import { toolUsage, type LogPage } from "@/features/system-logs/log-model";
import { ListToolbar } from "@/components/list-toolbar";
import { shellGutter } from "@/shell/content-frame";

interface McpOverview {
  bridge: "listening" | "failed" | "stopped";
  bridgeError: string | null;
  port: number | null;
  pid: number;
  sessionFile: string | null;
  sessionFilePresent: boolean;
  appVersion: string;
  mcpVersion: string;
  protocol: number;
  readOnly: boolean;
  webviewReady: boolean;
}

interface ClientSnippet {
  id: string;
  label: string;
  configPath: string;
  json: string;
}

interface McpClientConfig {
  executablePath: string;
  command: string;
  shimReady: boolean;
  shimError: string | null;
  snippets: ClientSnippet[];
  warnings: string[];
}

type ToolSpec = (typeof catalog.tools)[number];

const useStyles = makeStyles({
  page: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0, backgroundColor: tokens.colorNeutralBackground2 },
  tag: {
    padding: `0 ${tokens.spacingHorizontalS}`,
    height: "20px",
    display: "inline-flex",
    alignItems: "center",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  scroll: { flex: 1, minHeight: 0, overflow: "auto" },
  body: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalL, padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}` },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  cardHead: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS },
  grow: { flex: 1, minWidth: 0 },
  facts: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}` },
  fact: { display: "flex", flexDirection: "column", minWidth: 0 },
  key: { color: tokens.colorNeutralForeground3 },
  value: { fontSize: tokens.fontSizeBase300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  hint: { color: tokens.colorNeutralForeground3 },
  danger: { color: tokens.colorStatusDangerForeground1 },
  warning: { color: tokens.colorStatusWarningForeground1 },
  code: {
    margin: 0,
    padding: tokens.spacingHorizontalM,
    overflow: "auto",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    whiteSpace: "pre",
    color: tokens.colorNeutralForeground1,
  },
  result: { maxHeight: "360px", whiteSpace: "pre-wrap", overflowWrap: "anywhere" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightRegular },
  th: {
    textAlign: "left",
    height: "36px",
    padding: "6px 8px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
  },
  td: { padding: "6px 8px", borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  row: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  rowOn: { backgroundColor: tokens.colorNeutralBackground1Selected },
  controls: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
  args: { width: "100%", fontFamily: tokens.fontFamilyMonospace },
  split: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: tokens.spacingHorizontalL },
});

const TROUBLE: Array<[string, string]> = [
  ["APP_NOT_RUNNING", "XPlanner 앱을 실행한 뒤 다시 시도하세요. MCP는 실행 중인 앱의 데이터를 읽습니다."],
  ["APP_NOT_READY", "앱이 데이터를 불러오는 중입니다. 잠시 후 다시 시도하세요."],
  ["UNAUTHORIZED", "아래 「세션 다시 만들기」 후 클라이언트에서 다시 호출하세요."],
  ["BRIDGE_VERSION_MISMATCH", "앱과 MCP 실행 파일의 버전을 맞추세요. 스니펫을 다시 복사하면 맞춰집니다."],
  ["TIMEOUT", "앱 창이 멈추지 않았는지 확인하세요."],
  ["BUSY", "동시에 요청이 너무 많습니다. 잠시 후 다시 시도하세요."],
  ["TOO_LARGE", "limit·기간·플랜으로 범위를 좁히세요."],
];

function exampleArgs(tool: ToolSpec): string {
  const out: Record<string, unknown> = {};
  for (const p of tool.params) {
    if (!("required" in p) || !p.required) continue;
    out[p.name] = p.type === "id" ? "" : p.type === "string" ? "" : p.type === "limit" ? 20 : p.type === "boolean" ? false : "";
  }
  if (tool.params.some((p) => p.type === "limit")) out.limit = 20;
  return JSON.stringify(out, null, 2);
}

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(null), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);
  const copy = (key: string, text: string) => void navigator.clipboard?.writeText(text).then(() => setCopied(key));
  return { copied, copy };
}

export function McpConnectionView() {
  const styles = useStyles();
  if (!isDesktop()) {
    return (
      <div className={styles.page}>
        <div className={styles.scroll} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <GhostEmptyState title="데스크탑 앱에서 연결할 수 있어요" description="이 기기의 데스크탑 앱에 연결합니다." />
        </div>
      </div>
    );
  }
  return <DesktopMcp />;
}

function DesktopMcp() {
  const styles = useStyles();
  const status = useConnectionStatus();
  const [overview, setOverview] = useState<McpOverview | null>(null);
  const [config, setConfig] = useState<McpClientConfig | null>(null);
  const [usage, setUsage] = useState<Record<string, { count: number; lastAt: string }>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedTool, setSelectedTool] = useState<string>(catalog.tools[1]?.name ?? catalog.tools[0]!.name);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [regenMsg, setRegenMsg] = useState<string | null>(null);
  const { copied, copy } = useCopy();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [o, c, page] = await Promise.all([
        invoke<McpOverview>("get_mcp_overview"),
        invoke<McpClientConfig>("get_mcp_client_config"),
        invoke<LogPage>("read_activity_log_page", { query: { group: "mcp", limit: 2000 } }),
      ]);
      setOverview(o);
      setConfig(c);
      setUsage(toolUsage(page.items));
    } catch {
      setLoadError("연결 정보를 읽지 못했습니다");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const tool = catalog.tools.find((t) => t.name === selectedTool) ?? catalog.tools[0]!;
  const bridgeText = overview ? (overview.bridge === "listening" ? `대기 중 · 127.0.0.1:${overview.port}` : overview.bridge === "failed" ? "시작하지 못함" : "멈춤") : "확인 중";

  return (
    <div className={styles.page}>
      <ListToolbar
        filters={
          <>
            <ConnectionBadge status={status} />
            <span className={styles.tag}>읽기 전용</span>
          </>
        }
        tools={
          <>
            <FuiButton appearance="subtle" icon={<ArrowClockwise16Regular />} onClick={() => void load()}>
              새로 고침
            </FuiButton>
            <FuiButton appearance="subtle" onClick={() => useSettingsHub.getState().open("app", "logs")}>
              로그 보기
            </FuiButton>
          </>
        }
      />
      <div className={styles.scroll}>
        <div className={styles.body}>
          {loadError && (
            <Caption1 className={styles.danger} role="alert">
              {loadError}
            </Caption1>
          )}

          <section className={styles.card} aria-labelledby="mcp-status">
            <SectionTitle id="mcp-status">상태</SectionTitle>
            <div className={styles.facts}>
              <Fact label="브리지" value={bridgeText} tone={overview?.bridge === "failed" ? "danger" : undefined} />
              <Fact label="앱 화면" value={overview ? (overview.webviewReady ? "준비됨" : "불러오는 중") : "—"} />
              <Fact label="세션 파일" value={overview ? (overview.sessionFilePresent ? "있음" : "없음") : "—"} title={overview?.sessionFile ?? undefined} />
              <Fact label="앱 버전" value={overview?.appVersion ?? "—"} />
              <Fact label="MCP 버전 · 프로토콜" value={overview ? `${overview.mcpVersion} · v${overview.protocol}` : "—"} />
            </div>
            {overview?.bridgeError && <Caption1 className={styles.danger}>{overview.bridgeError}</Caption1>}
            {config?.warnings.map((w) => (
              <Caption1 key={w} className={styles.warning}>
                {w}
              </Caption1>
            ))}
          </section>

          <section className={styles.card} aria-labelledby="mcp-connect">
            <SectionTitle id="mcp-connect">연결 방법</SectionTitle>
            <Caption1 className={styles.hint}>클라이언트 설정 파일에 아래 내용을 넣으세요. 앱을 옮기거나 새 버전으로 바꿔도 이 경로는 그대로입니다.</Caption1>
            {config && (
              <Tabs defaultValue={config.snippets[0]?.id ?? "cursor"}>
                <TabsList variant="line" className="h-8 w-full justify-start bg-transparent p-0">
                  {config.snippets.map((s) => (
                    <TabsTrigger key={s.id} value={s.id} className="h-7 flex-none px-2 text-body">
                      {s.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {config.snippets.map((s) => (
                  <TabsContent key={s.id} value={s.id} className="flex flex-col gap-2 pt-2">
                    <div className={styles.cardHead}>
                      <Caption1 className={mergeClasses(styles.hint, styles.grow)}>{s.configPath ? `설정 파일: ${s.configPath}` : "클라이언트의 MCP 서버 설정에 넣으세요"}</Caption1>
                      <FuiButton appearance="primary" icon={copied === s.id ? <Checkmark16Regular /> : <Copy16Regular />} onClick={() => copy(s.id, s.json)}>
                        {copied === s.id ? "복사했어요" : "복사"}
                      </FuiButton>
                    </div>
                    <pre className={styles.code} aria-label={`${s.label} 설정`}>
                      {s.json}
                    </pre>
                  </TabsContent>
                ))}
              </Tabs>
            )}
            {config && !config.shimReady && <Caption1 className={styles.warning}>고정 경로를 만들지 못해 실행 파일 경로를 직접 넣었습니다. 앱을 옮기면 다시 복사하세요.</Caption1>}
          </section>

          <section className={styles.card} aria-labelledby="mcp-tools">
            <SectionTitle id="mcp-tools">도구</SectionTitle>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th} style={{ width: 200 }}>
                    이름
                  </th>
                  <th className={styles.th}>설명</th>
                  <th className={styles.th} style={{ width: 120 }}>
                    최근 호출
                  </th>
                  <th className={mergeClasses(styles.th, styles.num)} style={{ width: 72 }}>
                    호출 수
                  </th>
                </tr>
              </thead>
              <tbody>
                {catalog.tools.map((t) => {
                  const u = usage[t.name];
                  return (
                    <tr
                      key={t.name}
                      className={mergeClasses(styles.row, selectedTool === t.name && styles.rowOn)}
                      tabIndex={0}
                      aria-selected={selectedTool === t.name}
                      onClick={() => setSelectedTool(t.name)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedTool(t.name);
                        }
                      }}
                    >
                      <td className={styles.td} title={t.name}>
                        {t.name}
                      </td>
                      <td className={styles.td} title={t.description}>
                        {t.description}
                      </td>
                      <td className={styles.td}>{u ? formatAgo(u.lastAt) : "—"}</td>
                      <td className={mergeClasses(styles.td, styles.num)}>{u ? u.count.toLocaleString("ko-KR") : 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          <Playground key={tool.name} tool={tool} onToolChange={setSelectedTool} />

          <section className={styles.card} aria-labelledby="mcp-trouble">
            <SectionTitle id="mcp-trouble">문제 해결</SectionTitle>
            <table className={styles.table}>
              <tbody>
                {TROUBLE.map(([code, text]) => (
                  <tr key={code}>
                    <td className={styles.td} style={{ width: 220 }}>
                      {code}
                    </td>
                    <td className={styles.td} title={text} style={{ whiteSpace: "normal" }}>
                      {text}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={styles.cardHead}>
              <Caption1 className={mergeClasses(styles.hint, styles.grow)}>토큰이 새어 나갔다고 생각되면 세션을 다시 만드세요. 연결된 클라이언트는 다음 호출에서 새 세션을 읽습니다.</Caption1>
              <FuiButton appearance="secondary" disabled={overview?.bridge !== "listening"} onClick={() => setConfirmRegen(true)}>
                세션 다시 만들기
              </FuiButton>
            </div>
            {regenMsg && <Caption1 role="status">{regenMsg}</Caption1>}
          </section>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRegen}
        title="세션 다시 만들기"
        message="「MCP 세션」을 다시 만들까요? 연결된 클라이언트의 다음 호출이 한 번 실패할 수 있습니다."
        confirmLabel="다시 만들기"
        onCancel={() => setConfirmRegen(false)}
        onConfirm={async () => {
          setConfirmRegen(false);
          try {
            await invoke("regenerate_mcp_session");
            setRegenMsg("세션을 다시 만들었습니다");
          } catch {
            setRegenMsg("세션을 다시 만들지 못했습니다");
          }
          void load();
        }}
      />
    </div>
  );
}

function Fact({ label, value, title, tone }: { label: string; value: string; title?: string; tone?: "danger" }) {
  const styles = useStyles();
  return (
    <div className={styles.fact}>
      <Caption1 className={styles.key}>{label}</Caption1>
      <span className={mergeClasses(styles.value, tone === "danger" && styles.danger)} title={title ?? value}>
        {value}
      </span>
    </div>
  );
}

function Playground({ tool, onToolChange }: { tool: ToolSpec; onToolChange: (name: string) => void }) {
  const styles = useStyles();
  const [args, setArgs] = useState(() => exampleArgs(tool));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const options = useMemo(() => catalog.tools.map((t) => ({ value: t.name, label: t.name })), []);
  const params = tool.params.map((p) => `${p.name}${"required" in p && p.required ? "*" : ""}: ${p.type}`).join(", ");

  const run = async () => {
    let parsed: unknown = {};
    try {
      parsed = args.trim() ? JSON.parse(args) : {};
      setParseError(null);
    } catch {
      setParseError("인자가 올바른 JSON이 아닙니다");
      return;
    }
    setRunning(true);
    try {
      const out = await invoke<unknown>("invoke_mcp_playground", { tool: tool.name, args: parsed });
      setResult(JSON.stringify(out, null, 2));
    } catch {
      setResult(null);
      setParseError("실행하지 못했습니다");
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className={styles.card} aria-labelledby="mcp-playground">
      <div className={styles.cardHead}>
        <SectionTitle id="mcp-playground" className={styles.grow}>
          플레이그라운드
        </SectionTitle>
        <Caption1 className={styles.hint}>실제 MCP와 같은 경로로 실행하고 로그에 「앱 화면」으로 남깁니다</Caption1>
      </div>
      <div className={styles.controls}>
        <FuiDropdown value={tool.name} options={options} onValueChange={onToolChange} aria-label="도구" />
        <Caption1 className={styles.hint}>{params || "인자 없음"}</Caption1>
      </div>
      <div className={styles.split}>
        <div className="flex flex-col gap-1">
          <FuiTextarea className={styles.args} rows={8} value={args} onChange={(_, d) => setArgs(d.value)} aria-label="인자(JSON)" aria-invalid={!!parseError} />
          {parseError && (
            <Caption1 className={styles.danger} role="alert">
              {parseError}
            </Caption1>
          )}
          <div>
            <FuiButton appearance="secondary" icon={<Play16Regular />} disabled={running} onClick={() => void run()}>
              {running ? "실행 중…" : "실행"}
            </FuiButton>
          </div>
        </div>
        <pre className={mergeClasses(styles.code, styles.result)} aria-label="결과" aria-busy={running}>
          {result ?? "결과가 여기에 보입니다"}
        </pre>
      </div>
    </section>
  );
}
